import './TicketIaScoreSummary.scss';

import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Tag, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import InfoModal from '@/components/general/InfoModal';
import { Ticket } from '@/model/Ticket/Ticket';

interface TicketIaScoreSummaryProps {
  ticket?: Ticket;
}

const getScoreColor = (puntajeFinal: number) => {
  if (puntajeFinal >= 80) return 'green';
  if (puntajeFinal >= 50) return 'gold';

  return 'red';
};

// Metadata: resumen compacto del puntaje de IA con acceso al detalle completo vía modal.
const TicketIaScoreSummary = (props: TicketIaScoreSummaryProps) => {
  const { t } = useTranslation();
  const { ticket } = props;
  const iaScore = ticket?.ia_score;
  const failedCriteria = useMemo(
    () => iaScore?.criterios_evaluados.filter(criterio => criterio.estado === 'no_cumple') ?? [],
    [iaScore],
  );

  if (!iaScore) return null;

  return (
    <Flex vertical gap={4} className="TicketIaScoreSummary">
      <Flex align="center" gap={8} className="TicketIaScoreSummaryHeader">
        <b>{t('Puntaje IA')}: </b>
        <Tag
          style={{ margin: 0 }}
          color={getScoreColor(iaScore.puntaje_final)}>{`${iaScore.puntaje_final}%`}</Tag>
        <span>({`${iaScore.puntos_obtenidos}/${iaScore.puntos_totales_posibles}`})</span>
        <InfoModal
          title={t('Evaluación de cierre (IA)')}
          openButton={
            <Tooltip title={t('Ver evaluación de IA')}>
              <Button
                className="TicketIaScoreSummaryInfoButton"
                type="text"
                size="small"
                icon={
                  failedCriteria.length > 0 ? (
                    <WarningOutlined style={{ color: '#faad14' }} />
                  ) : (
                    <InfoCircleOutlined />
                  )
                }
              />
            </Tooltip>
          }
          message={
            <div>
              <b>{t('Criterios que no cumplen')}: </b>
              {failedCriteria.length === 0 ? (
                <div>{t('Sin observaciones')}</div>
              ) : (
                <Flex vertical gap={8} style={{ marginTop: 8 }}>
                  {failedCriteria.map(criterio => (
                    <Alert
                      key={criterio.criterio_id}
                      type="warning"
                      showIcon
                      message={criterio.criterio}
                      description={criterio.mensaje_usuario || undefined}
                    />
                  ))}
                </Flex>
              )}
            </div>
          }
        />
      </Flex>
      <div className="TicketIaScoreSummaryText">
        <b>{t('Problema inicial detectado')}: </b>
        <span>{iaScore.problema_inicial_detectado || '-'}</span>
      </div>
      <div className="TicketIaScoreSummaryText">
        <b>{t('Solución aplicada')}: </b>
        <span>{iaScore.solucion_aplicada || '-'}</span>
      </div>
    </Flex>
  );
};

export default TicketIaScoreSummary;
